/**
 * @since 2026-04-22
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { ConfigProvider, Table, theme as antdTheme } from "antd";

import ConfirmationDialog from "components/Shared/ConfirmationDialog";
import StatusText from "components/Shared/StatusText";
import { useNotification } from "components/Shared/Notifications";
import { deactivateOverride, deactivateOverridesMany, listFixJobs, startFixJob } from "services/api";
import "./FixJobHistoryDialog.scss";

const POLL_INTERVAL_MS = 1000;
const TERMINAL_STATES = new Set(["succeeded", "failed", "stale"]);

// Display-only relabel of the daily bar (raw value "1Day" is kept for routing/keys).
const formatBarSize = (barSize) => (barSize === "1Day" ? "1Day_EndOfDay" : barSize || "");

const STATE_SEVERITY = {
  succeeded: "validated",
  failed: "error",
  stale: "error",
  running: "running",
  queued: "queued",
};

const ITEM_SEVERITY = {
  ok: "validated",
  skipped: "na",
  errored: "error",
};

const formatTimestamp = (ts) => (ts ? String(ts) : "—");

const describeScope = (job) => {
  const n = (job.scope || []).length;
  if (n === 0) return "—";
  if (n === 1) {
    const { securityName, barSize } = job.scope[0];
    return `${securityName} / ${barSize}`;
  }
  return `${n} securities`;
};

const FixJobHistoryDialog = ({
  open, idToken, getToken, onClose, canRun = false,
  pendingOverrides = [], refetchPendingOverrides,
}) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeAllConfirmOpen, setRemoveAllConfirmOpen] = useState(false);
  const [removingAll, setRemovingAll] = useState(false);
  const [starting, setStarting] = useState(false);
  const { notifyError, notifyInfo, notifySuccess } = useNotification();

  useEffect(() => {
    if (!open || !idToken) return () => {};

    let cancelled = false;
    let timer = null;
    setLoading(true);

    const tick = async () => {
      try {
        const [response] = await Promise.all([
          listFixJobs(20, idToken, getToken),
          refetchPendingOverrides?.(),
        ]);
        if (cancelled) return;
        setJobs(response?.jobs || []);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoading(false);
        notifyError(e?.message || "Failed to load fix job history");
      } finally {
        if (!cancelled) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        }
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, idToken, getToken, notifyError, refetchPendingOverrides]);

  const handleRemoveOverride = useCallback(async (overrideId) => {
    try {
      await deactivateOverride(overrideId, idToken, getToken);
      await refetchPendingOverrides?.();
      notifySuccess("Override removed");
    } catch (e) {
      notifyError(e?.message || "Failed to remove override");
    }
  }, [idToken, getToken, notifyError, notifySuccess, refetchPendingOverrides]);

  const handleRemoveAllPending = useCallback(async () => {
    setRemoveAllConfirmOpen(false);
    setRemovingAll(true);
    try {
      const ids = pendingOverrides.map((o) => o.overrideId);
      const response = await deactivateOverridesMany(ids, idToken, getToken);
      await refetchPendingOverrides?.();
      const requested = response?.requested ?? ids.length;
      const deactivated = response?.deactivated ?? 0;
      if (deactivated === requested) {
        notifySuccess(`Removed ${deactivated} override${deactivated === 1 ? "" : "s"}`);
      } else {
        notifyError(`Removed ${deactivated} of ${requested}`);
      }
    } catch (e) {
      notifyError(e?.message || "Failed to remove pending overrides");
    } finally {
      setRemovingAll(false);
    }
  }, [pendingOverrides, idToken, getToken, notifyError, notifySuccess, refetchPendingOverrides]);

  const handleRunFixJob = useCallback(async () => {
    setConfirmOpen(false);
    setStarting(true);
    try {
      await startFixJob(
        { scope: "universe", reason: "Manual fix-job trigger from jobs panel" },
        idToken,
        getToken,
      );
      notifyInfo("Fix job queued");
      const response = await listFixJobs(20, idToken, getToken);
      setJobs(response?.jobs || []);
    } catch (e) {
      notifyError(e?.message || "Failed to start fix job");
    } finally {
      setStarting(false);
    }
  }, [idToken, getToken, notifyInfo, notifyError]);

  const hasActiveJob = useMemo(
    () => jobs.some((j) => !TERMINAL_STATES.has(j.state)),
    [jobs],
  );
  const pendingCount = pendingOverrides.length;

  const pendingColumns = useMemo(() => {
    const base = [
      { title: "Security", dataIndex: "securityName", key: "securityName", width: 140 },
      { title: "Bar size", dataIndex: "barSize", key: "barSize", width: 110, render: (v) => formatBarSize(v) },
      { title: "Kind", dataIndex: "kind", key: "kind", width: 110 },
      { title: "Check", dataIndex: "checkName", key: "checkName", width: 160,
        render: (v) => v || "—" },
      { title: "Value", key: "value", width: 160,
        render: (_, r) => {
          if (r.kind === "startDate") return r.value || "—";
          if (r.kind === "threshold") return `${r.parameterName} = ${r.value}`;
          if (r.kind === "dateRangeException") return `${r.startDate} → ${r.endDate}`;
          return r.value || "—";
        } },
      { title: "Created at", dataIndex: "createdAt", key: "createdAt", width: 160, render: formatTimestamp },
      { title: "Reason", dataIndex: "reason", key: "reason", ellipsis: true },
    ];
    if (!canRun) return base;
    return [
      ...base,
      {
        title: "",
        key: "actions",
        width: 90,
        render: (_, r) => (
          <Button
            size="small"
            className="fixJobHistoryDialog__removeButton"
            onClick={() => handleRemoveOverride(r.overrideId)}
          >
            Remove
          </Button>
        ),
      },
    ];
  }, [canRun, handleRemoveOverride]);

  const columns = useMemo(() => [
    {
      title: "Requested at",
      dataIndex: "requestedAt",
      key: "requestedAt",
      width: 160,
      render: formatTimestamp,
    },
    {
      title: "Finished at",
      dataIndex: "finishedAt",
      key: "finishedAt",
      width: 160,
      render: formatTimestamp,
    },
    {
      title: "Progress",
      key: "progress",
      width: 160,
      render: (_, job) => {
        const total = job.total || 0;
        const done = job.done || 0;
        const errored = job.errored || 0;
        const skipped = job.skipped || 0;
        const tail = errored || skipped
          ? ` (${errored} err, ${skipped} skip)`
          : "";
        return `${done}/${total}${tail}`;
      },
    },
    {
      title: "Scope",
      key: "scope",
      width: 160,
      render: (_, job) => describeScope(job),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 140,
      render: (state, job) => {
        const severity = STATE_SEVERITY[state] || "info";
        const label = state === "running" && job.total
          ? `RUNNING ${job.done || 0}/${job.total}`
          : state.toUpperCase();
        return <StatusText severity={severity} label={label} />;
      },
    },
  ], []);

  const expandedRowRender = useCallback((job) => {
    const items = job.items || [];
    const scope = job.scope || [];
    const doneKeys = new Set(items.map((it) => `${it.securityName}|${it.barSize}`));
    const currentKey = job.currentItem
      ? `${job.currentItem.securityName}|${job.currentItem.barSize}`
      : null;
    const pending = scope
      .filter((s) => !doneKeys.has(`${s.securityName}|${s.barSize}`))
      .map((s) => {
        const key = `${s.securityName}|${s.barSize}`;
        return {
          securityName: s.securityName,
          barSize: s.barSize,
          state: key === currentKey ? "running" : "queued",
          error: "",
        };
      });
    const rows = [
      ...items.map((it, i) => ({ ...it, __id: `done-${i}` })),
      ...pending.map((it, i) => ({ ...it, __id: `pending-${i}` })),
    ];
    const itemColumns = [
      {
        title: "SecurityName",
        dataIndex: "securityName",
        key: "securityName",
        width: 150,
      },
      {
        title: "BarSize",
        dataIndex: "barSize",
        key: "barSize",
        width: 120,
        render: (v) => formatBarSize(v),
      },
      {
        title: "State",
        dataIndex: "state",
        key: "state",
        width: 140,
        render: (state) => {
          const severity = ITEM_SEVERITY[state] || state;
          return <StatusText severity={severity} label={state} />;
        },
      },
      {
        title: "Error",
        dataIndex: "error",
        key: "error",
        render: (err) => (err ? <Typography variant="caption" className="fixJobHistoryDialog__error">{err}</Typography> : ""),
      },
    ];
    if (rows.length === 0) {
      return <Typography variant="body2" className="fixJobHistoryDialog__emptyItems">No items yet.</Typography>;
    }
    return (
      <Table
        className="fixJobHistoryDialog__itemsTable"
        columns={itemColumns}
        dataSource={rows}
        rowKey="__id"
        pagination={false}
        size="small"
      />
    );
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="fixJobHistoryDialog"
      PaperProps={{ className: "dialogPaper" }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle className="dialogTitle">
        <Typography variant="h6" component="div">
          Recent Fix Jobs
        </Typography>
      </DialogTitle>
      <DialogContent className="dialogContent">
        <ConfigProvider
          theme={{
            algorithm: antdTheme.darkAlgorithm,
            token: { colorPrimary: "#78bcfb" },
          }}
        >
          <Typography variant="subtitle2" className="fixJobHistoryDialog__sectionTitle">
            {pendingCount > 0
              ? `Pending overrides (${pendingCount}) — waiting for a fix job`
              : "No pending overrides"}
          </Typography>
          {pendingCount > 0 && (
            <Table
              className="fixJobHistoryDialog__pendingTable"
              columns={pendingColumns}
              dataSource={pendingOverrides}
              rowKey="overrideId"
              pagination={false}
              size="small"
            />
          )}
          <Typography
            variant="subtitle2"
            className="fixJobHistoryDialog__sectionTitle fixJobHistoryDialog__sectionTitle--secondary"
          >
            Recent fix jobs
          </Typography>
          <Table
            className="fixJobHistoryDialog__table"
            columns={columns}
            dataSource={jobs}
            rowKey="jobId"
            loading={loading}
            pagination={false}
            size="middle"
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpandedRowsChange: setExpandedRowKeys,
            }}
          />
        </ConfigProvider>
      </DialogContent>
      <DialogActions className="dialogActions">
        {canRun && pendingCount > 0 && (
          <Button
            onClick={() => setRemoveAllConfirmOpen(true)}
            className="fixJobHistoryDialog__removeAllButton"
            disabled={removingAll}
          >
            {removingAll ? "Removing…" : "Remove all pending jobs"}
          </Button>
        )}
        {canRun && (
          <Button
            onClick={() => setConfirmOpen(true)}
            className="confirmButton primary"
            variant="contained"
            disabled={pendingCount === 0 || hasActiveJob || starting}
          >
            {starting ? "Starting…" : hasActiveJob ? "Fix job running…" : "Run Fix Job"}
          </Button>
        )}
        <Button onClick={onClose} className="cancelButton">
          Close
        </Button>
      </DialogActions>
      <ConfirmationDialog
        open={confirmOpen}
        title="Run fix job?"
        message={`Re-run reviews for every security with pending overrides (${pendingCount}). This may take several minutes.`}
        confirmText="Run"
        onConfirm={handleRunFixJob}
        onCancel={() => setConfirmOpen(false)}
      />
      <ConfirmationDialog
        open={removeAllConfirmOpen}
        title="Remove all pending overrides?"
        message={`Deactivate all ${pendingCount} pending override${pendingCount === 1 ? "" : "s"}. This cannot be undone.`}
        confirmText="Remove all"
        onConfirm={handleRemoveAllPending}
        onCancel={() => setRemoveAllConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default FixJobHistoryDialog;
